import { request } from "../../utils/request";

// ==================== 请求/响应类型 ====================

export interface VoiceRefVO {
  voiceId: number;
  /** 音色来源：PUBLIC_VOICE_TONE / USER_VOICE_TONE / SUBTITLE / NONE */
  voiceSourceType: string;
}

export interface AudioConfigVO {
  format?: "WAV" | "MP3" | "PCM" | "OGG_OPUS";
  sampleRate?: number; // 8000/16000/24000/32000/44100/48000
  speechRate?: number; // -50 ~ 100，0=正常
  loudnessRate?: number; // -50 ~ 100，0=正常
  pitchRate?: number; // -12 ~ 12，0=正常
  enableSubtitle?: boolean;
}

export interface VoicePerformanceCreateReq {
  script: string;
  referenceType: "VOICES" | "IMAGE" | "TEXT";
  voices?: VoiceRefVO[];
  audioConfig?: AudioConfigVO;
}

export interface VoicePerformanceCreateResp {
  taskId: number;
  status: string;
  outputAudioUrl: string;
  duration: number;
  originalDuration?: number;
  referenceImageUrl?: string;
  subtitle?: Record<string, unknown>;
}

export interface VoiceProfileListResp {
  id: number;
  displayName: string;
  description?: string;
  provider: string;
  sourceType: string;
  gender: string;
  scene: string;
  avatarUrl: string;
  previewAudioUrl: string;
}

export interface VoicePerformanceTaskItem {
  id: number;
  taskId?: number;
  referenceType?: string;
  referenceImageUrl?: string;
  script?: string;
  text?: string;
  outputAudioUrl?: string;
  audioUrl?: string;
  duration?: number;
  originalDuration?: number;
  voicesSnapshot?: string;
  resolvedTextPrompt?: string;
  subtitle?: Record<string, unknown>;
  status: string;
  errorCode?: string;
  errorMessage?: string;
  speechRate?: number;
  pitchRate?: number;
  loudnessRate?: number;
  createTime: string;
}

export interface VoicePerformanceTaskPageResp {
  list: VoicePerformanceTaskItem[];
  total: number;
}

export interface VoicePerformanceTaskPageParams {
  pageNo?: number;
  pageSize?: number;
  taskId?: number;
  status?: string;
  referenceType?: string;
  keyword?: string;
}

export interface VoicePerformanceTaskDeleteReq {
  taskIds: number[];
}

export interface VoicePerformanceTaskDeleteResp {
  deletedCount?: number;
  skippedIds?: number[];
}

// ==================== API 函数 ====================

/**
 * 声音演绎 - 公共音色列表
 * GET /voice-performance/voices
 */
export const voicePerformanceVoicesApi = (
  params?: { displayName?: string; gender?: string; scene?: string; provider?: string; sourceType?: string }
) =>
  request<{ code: number; data: VoiceProfileListResp[]; msg: string }>("/voice-performance/voices", {
    method: "GET",
    params,
  });

/**
 * 声音演绎 - 创建任务（JSON 方式：TEXT / VOICES 模式）
 * POST /voice-performance/generate
 */
export const voicePerformanceGenerateApi = (body: VoicePerformanceCreateReq) =>
  request<{ code: number; data: VoicePerformanceCreateResp; msg: string }>("/voice-performance/generate", {
    method: "POST",
    body,
  });

/**
 * 声音演绎 - 创建任务（IMAG 模式：multipart/form-data，JSON + 图片文件）
 * POST /voice-performance/generate
 * @param reqJson 请求 JSON 字符串
 * @param imageFile 图片文件
 */
export const voicePerformanceGenerateWithImageApi = (
  reqJson: string,
  imageFile: File
) => {
  const formData = new FormData();
  formData.append("request", new Blob([reqJson], { type: "application/json" }));
  formData.append("image", imageFile);
  return request<{ code: number; data: VoicePerformanceCreateResp; msg: string }>("/voice-performance/generate", {
    method: "POST",
    body: formData,
  });
};

/**
 * 声音演绎 - 任务查询（分页列表）
 * GET /voice-performance/tasks
 */
export const voicePerformanceTasksApi = (params?: VoicePerformanceTaskPageParams) =>
  request<{ code: number; data: VoicePerformanceTaskPageResp; msg: string }>("/voice-performance/tasks", {
    method: "GET",
    params,
  });

/**
 * 声音演绎 - 批量删除我的任务
 * DELETE /voice-performance/tasks
 */
export const voicePerformanceTaskDeleteApi = (body: VoicePerformanceTaskDeleteReq) =>
  request<{ code: number; data?: VoicePerformanceTaskDeleteResp; msg?: string }>("/voice-performance/tasks", {
    method: "DELETE",
    body,
  });

// ==================== 润色 ====================

export interface VoicePerformancePolishReq {
  referenceType: "VOICES" | "TEXT" | "IMAGE";
  script: string;
}

export interface VoicePerformancePolishResp {
  optimizedPrompt: string;
  originalPrompt: string;
  referenceType: string;
  tokensUsed: number;
  model: string;
  latencyMs: number;
}

/**
 * 声音演绎 - AI 润色脚本
 * POST /voice-performance/optimize-prompt
 * request: JSON 字符串（含 referenceType、script）
 * image: 可选文件（仅 IMAGE 模式）
 */
export const voicePerformancePolishApi = (
  body: VoicePerformancePolishReq,
  image?: File,
) => {
  const formData = new FormData();
  formData.append(
    "request",
    new Blob([JSON.stringify(body)], { type: "application/json" }),
  );
  if (image) formData.append("image", image);
  return request<{ code: number; data: VoicePerformancePolishResp; msg: string }>(
    "/voice-performance/optimize-prompt",
    { method: "POST", body: formData },
  );
};

// ==================== 模板 ====================

export interface VoicePerformanceTemplate {
  id: number;
  title: string;
  description?: string;
  script: string;
  voiceIds?: number[] | string;
  coverImageUrl?: string;
  sampleAudioUrl?: string;
}

/**
 * 声音演绎 - 模板列表
 * GET /voice-performance/templates
 */
export const voicePerformanceTemplatesApi = () =>
  request<{ code: number; data: VoicePerformanceTemplate[]; msg: string }>("/voice-performance/templates", {
    method: "GET",
  });
